//#region 型定義
/**
 * サポートされている入力ファイル形式（拡張子）の型定義
 */
export type DrawingInputFormat =
  | "mnd" // MarkⅡ
  | "ai" // Adobe AI Files
  | "fxd" // CADSUPER FX
  | "fxs" // CADSUPER FX シンボル
  | "jx" // CADSUPER JX
  | "sym" // CADSUPER シンボル
  | "sx" // CADSUPER SX
  | "dwg" // DWG
  | "dxf" // DXF
  | "ed" // EASYDRAW
  | "edp" // EASYDRAW Pro
  | "sbl" // EASYDRAW シンボル
  | "hnd" // Handy
  | "hsd" // Handy シンボル
  | "hgl" // HPGL
  | "igs" // IGES(2D)
  | "jwc" // Jw_cad DOS版
  | "jws" // Jw_cad for Windows シンボル
  | "jwk" // Jw_cad シンボル
  | "jww" // Jw_cad for Windows
  | "pdf" // PDF Files
  | "p21" // SXF(P21)
  | "p2z" // SXF(P2z)
  | "sfz" // SXFシンボル
  | "sfc" // SFC(SFC)
  | "emf" // 拡張メタファイル
  | "wmf" // 標準メタファイル
  | "NC"; // NCファイル

/**
 * サポートされている出力ファイル形式（拡張子）の型定義
 */
export type DrawingOutputFormat =
  | "MND" // MarkⅡ
  | "fxd" // CADSUPER FX
  | "fxs" // CADSUPER FX シンボル
  | "jx" // CADSUPER JX
  | "sym" // CADSUPER シンボル
  | "sx" // CADSUPER SX
  | "dwg" // AutoCAD DWG (複数バージョン)
  | "dxf" // AutoCAD DXF (複数バージョン)
  | "ed" // EASYDRAW
  | "sbl" // EASYDRAW シンボル
  | "hnd" // Handy
  | "hsd" // Handy シンボル
  | "igs" // IGES(2D)
  | "jwc" // Jw_cad (DOS)
  | "jww" // Jw_cad for Windows
  | "pdf" // PDF Files
  | "p21z" // SXF(P21) Zip
  | "p21" // SXF(P21)
  | "sfcz" // SXF(SFC) Zip
  | "sfc" // SXF(SFC)
  | "svg"; // SVG Files

/**
 * AutoCAD DWGバージョンの型定義
 */
export type DwgVersion =
  | "AutoCAD 2004 DWG"
  | "AutoCAD DWG"
  | "AutoCAD R13J DWG"
  | "AutoCAD R14 DWG"
  | "AutoCAD R12J/GX-5 DWG"
  | "AutoCAD GX-3 DWG";

/**
 * AutoCAD DXFバージョンの型定義
 */
export type DxfVersion =
  | "AutoCAD 2000 DXF"
  | "AutoCAD DXF"
  | "AutoCAD R13J DXF"
  | "AutoCAD R14 DXF"
  | "AutoCAD R12J/GX-5 DXF"
  | "AutoCAD GX-3 DXF";

/**
 * 図面変換パラメータのインターフェース
 */
export interface DrawingConversionParameters {
  /** 入力ファイルパス */
  inputFilePath: string;
  /** 出力ファイル形式 */
  outputFormat: DrawingOutputFormat;
  /** 出力ファイルパス */
  outputFilePath: string;
  /** 変換品質 (1-100) */
  quality?: number;
  /** DWG/DXFのバージョン */
  version?: string;
}

/**
 * 図面変換リクエストのインターフェース
 */
export interface DrawingConversionRequest {
  /** 変換パラメータ */
  conversionParameters: DrawingConversionParameters;
}

/**
 * 図面変換レスポンスのインターフェース
 */
export interface DrawingConversionResponse {
  /** 処理成功フラグ */
  success: boolean;
  /** メッセージ */
  message: string;
  /** 変換後のファイルパス */
  convertedFilePath: string | null;
  /** エラー詳細 */
  errorDetails?: string;
}
//#endregion

//#region 定数定義
/**
 * 入力ファイル形式の定義
 */
export const DRAWING_INPUT_FORMATS: {
  value: DrawingInputFormat;
  label: string;
}[] = [
  { value: "mnd", label: "MarkⅡ (.mnd)" },
  { value: "ai", label: "Adobe AI Files (.ai)" },
  { value: "fxd", label: "CADSUPER FX (.fxd)" },
  { value: "fxs", label: "CADSUPER FX シンボル (.fxs)" },
  { value: "jx", label: "CADSUPER JX (.jx)" },
  { value: "sym", label: "CADSUPER シンボル (.sym)" },
  { value: "sx", label: "CADSUPER SX (.sx)" },
  { value: "dwg", label: "DWG (.dwg)" },
  { value: "dxf", label: "DXF (.dxf)" },
  { value: "ed", label: "EASYDRAW (.ed)" },
  { value: "edp", label: "EASYDRAW Pro (.edp)" },
  { value: "sbl", label: "EASYDRAW シンボル (.sbl)" },
  { value: "hnd", label: "Handy (.hnd)" },
  { value: "hsd", label: "Handy シンボル (.hsd)" },
  { value: "hgl", label: "HPGL (.hgl)" },
  { value: "igs", label: "IGES(2D) (.igs)" },
  { value: "jwc", label: "Jw_cad DOS版 (.jwc)" },
  { value: "jws", label: "Jw_cad for Windows シンボル (.jws)" },
  { value: "jwk", label: "Jw_cad シンボル (.jwk)" },
  { value: "jww", label: "Jw_cad for Windows (.jww)" },
  { value: "pdf", label: "PDF Files (.pdf)" },
  { value: "p21", label: "SXF(P21) (.p21)" },
  { value: "p2z", label: "SXF(P2z) (.p2z)" },
  { value: "sfz", label: "SXFシンボル (.sfz)" },
  { value: "sfc", label: "SFC(SFC) (.sfc)" },
  { value: "emf", label: "拡張メタファイル (.emf)" },
  { value: "wmf", label: "標準メタファイル (.wmf)" },
  { value: "NC", label: "NCファイル (.NC)" },
];

/**
 * 出力ファイル形式の定義
 */
export const DRAWING_OUTPUT_FORMATS: {
  value: DrawingOutputFormat;
  label: string;
  versions?: string[];
}[] = [
  { value: "MND", label: "MarkⅡ (.MND)" },
  { value: "fxd", label: "CADSUPER FX (.fxd)" },
  { value: "fxs", label: "CADSUPER FX シンボル (.fxs)" },
  { value: "jx", label: "CADSUPER JX (.jx)" },
  { value: "sym", label: "CADSUPER シンボル (.sym)" },
  { value: "sx", label: "CADSUPER SX (.sx)" },
  {
    value: "dwg",
    label: "AutoCAD DWG (.dwg)",
    versions: [
      "AutoCAD 2004 DWG",
      "AutoCAD DWG",
      "AutoCAD R13J DWG",
      "AutoCAD R14 DWG",
      "AutoCAD R12J/GX-5 DWG",
      "AutoCAD GX-3 DWG",
    ],
  },
  {
    value: "dxf",
    label: "AutoCAD DXF (.dxf)",
    versions: [
      "AutoCAD 2000 DXF",
      "AutoCAD DXF",
      "AutoCAD R13J DXF",
      "AutoCAD R14 DXF",
      "AutoCAD R12J/GX-5 DXF",
      "AutoCAD GX-3 DXF",
    ],
  },
  { value: "ed", label: "EASYDRAW (.ed)" },
  { value: "sbl", label: "EASYDRAW シンボル (.sbl)" },
  { value: "hnd", label: "Handy (.hnd)" },
  { value: "hsd", label: "Handy シンボル (.hsd)" },
  { value: "igs", label: "IGES(2D) (.igs)" },
  { value: "jwc", label: "Jw_cad (DOS) (.jwc)" },
  { value: "jww", label: "Jw_cad for Windows (.jww)" },
  { value: "pdf", label: "PDF Files (.pdf)" },
  { value: "p21z", label: "SXF(P21) Zip (.p21z)" },
  { value: "p21", label: "SXF(P21) (.p21)" },
  { value: "sfcz", label: "SXF(SFC) Zip (.sfcz)" },
  { value: "sfc", label: "SXF(SFC) (.sfc)" },
  { value: "svg", label: "SVG Files (.svg)" },
];
//#endregion

//#region ヘルパー関数
/**
 * ファイル拡張子から対応するMIMEタイプを取得する
 * @param extension ファイル拡張子
 * @returns MIMEタイプ文字列
 */
export const getDrawingMimeType = (
  extension: DrawingInputFormat | DrawingOutputFormat
): string => {
  const mimeTypes: Record<string, string> = {
    dwg: "application/acad",
    dxf: "application/dxf",
    pdf: "application/pdf",
    svg: "image/svg+xml",
    // その他の形式はバイナリとして扱う
    default: "application/octet-stream",
  };

  return mimeTypes[extension.toLowerCase()] || mimeTypes.default;
};

/**
 * ファイル名から拡張子を取得する
 * @param filename ファイル名
 * @returns ファイル拡張子（"."を含まない）
 */
export const getFileExtension = (filename: string): string => {
  return filename.split(".").pop()?.toLowerCase() || "";
};

/**
 * 入力ファイル形式が対応しているかチェックする
 * @param extension チェックする拡張子
 * @returns 対応している場合はtrue
 */
export const isValidInputFormat = (extension: string): boolean => {
  return DRAWING_INPUT_FORMATS.some(
    (format) => format.value.toLowerCase() === extension.toLowerCase()
  );
};

/**
 * 出力ファイル形式が対応しているかチェックする
 * @param extension チェックする拡張子
 * @returns 対応している場合はtrue
 */
export const isValidOutputFormat = (extension: string): boolean => {
  return DRAWING_OUTPUT_FORMATS.some(
    (format) => format.value.toLowerCase() === extension.toLowerCase()
  );
};
//#endregion
